import { createBrowserRouter, Navigate } from 'react-router';

import { CourseAccessRoute } from '@/auth/CourseAccessRoute';
import { ProfileCompletionRoute } from '@/auth/ProfileCompletionRoute';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { ErrorScreen } from '@/components/ErrorScreen';
import { ContentPage } from '@/features/content/ContentPage';
import { ContentEditRedirect } from '@/features/content/editor/ContentEditRedirect';
import { ChapterPdfPrintPage } from '@/features/content/pdfExport/ChapterPdfPrintPage';
import { CreateCoursePage } from '@/features/courses/CreateCoursePage';
import { CourseHomePage } from '@/features/courses/CourseHomePage';
import { CourseListPage } from '@/features/courses/CourseListPage';
import { AssignmentQuestionsPage } from '@/features/gradebook/AssignmentQuestionsPage';
import { GradebookPage } from '@/features/gradebook/GradebookPage';
import { StudentDetailPage } from '@/features/gradebook/StudentDetailPage';
import { MarketingLayout } from '@/features/marketing/MarketingLayout';
import { AboutPage } from '@/features/marketing/pages/AboutPage';
import { AccessibilityPage } from '@/features/marketing/pages/AccessibilityPage';
import { AIPage } from '@/features/marketing/pages/AIPage';
import { ContactPage } from '@/features/marketing/pages/ContactPage';
import { FacultyPage } from '@/features/marketing/pages/FacultyPage';
import { HomePage } from '@/features/marketing/pages/HomePage';
import { HumanitiesPage } from '@/features/marketing/pages/HumanitiesPage';
import { InstitutionsPage } from '@/features/marketing/pages/InstitutionsPage';
import { KaiCoachPage } from '@/features/marketing/pages/KaiCoachPage';
import { PressPage } from '@/features/marketing/pages/PressPage';
import { PrivacyPage } from '@/features/marketing/pages/PrivacyPage';
import { ResearchPage } from '@/features/marketing/pages/ResearchPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { SupportArticlePage } from '@/features/support/SupportArticlePage';
import { SupportIndexPage } from '@/features/support/SupportIndexPage';
import { SupportConsoleGate } from '@/features/supportConsole/SupportConsoleGate';
import { SupportConsolePage } from '@/features/supportConsole/SupportConsolePage';
import { ProfileSetupPage } from '@/features/profile/ProfileSetupPage';
import { TourProvider } from '@/features/productTours/TourProvider';
import {
  CHILD_ROUTES_STAY_ON_THIS_PAGE,
  LoadNewVersionOnPageChange,
} from '@/pwa/LoadNewVersionOnPageChange';

import { AppShell } from './AppShell';
import { LoginPage } from './LoginPage';
import { NotFound } from './NotFound';
import { SignupPage } from './SignupPage';
import { StaffInvitePage } from './StaffInvitePage';

const marketingPageRoutes = [
  { path: 'faculty', element: <FacultyPage /> },
  { path: 'institutions', element: <InstitutionsPage /> },
  { path: 'ai', element: <AIPage /> },
  { path: 'kai-coach', element: <KaiCoachPage /> },
  { path: 'research', element: <ResearchPage /> },
  { path: 'about', element: <AboutPage /> },
  { path: 'humanities', element: <HumanitiesPage /> },
  { path: 'press', element: <PressPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'privacy', element: <PrivacyPage /> },
  { path: 'accessibility', element: <AccessibilityPage /> },
] as const;

// Pages the footer once promised and that were never written (#175): an old
// link to one lands where the site's links for it now go. Blog and Careers
// went with their links, and are unknown addresses.
const marketingRedirectRoutes = [
  { path: 'pricing', element: <Navigate replace to="/institutions#pricing" /> },
  { path: 'subjects', element: <Navigate replace to="/faculty#subjects" /> },
  { path: 'subjects/:slug', element: <Navigate replace to="/faculty#subjects" /> },
  { path: 'case-studies', element: <Navigate replace to="/press" /> },
  { path: 'security', element: <Navigate replace to="/contact" /> },
] as const;

export const appRoutes = [
  {
    element: <MarketingLayout />,
    errorElement: <ErrorScreen message="Unable to render the marketing site." />,
    children: [
      { index: true, element: <HomePage /> },
      ...marketingPageRoutes,
      ...marketingRedirectRoutes,
      // The help site: two indexes and one page per inventory entry. Static
      // segments outrank the dynamic one, so /help/instructors is never a slug.
      { path: 'help', element: <SupportIndexPage audience="student" /> },
      { path: 'help/instructors', element: <SupportIndexPage audience="instructor" /> },
      { path: 'help/:slug', element: <SupportArticlePage /> },
    ],
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/signup',
    element: <SignupPage />,
  },
  // A course staff invite (/invite#<key>): public, like sign-in, because the
  // invitee may not have an account yet.
  {
    path: '/invite',
    element: <StaffInvitePage />,
  },
  {
    path: '/profile/setup',
    element: (
      <ProtectedRoute>
        <ProfileSetupPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/app/courses/:courseId/:contentId/print',
    element: (
      <ProtectedRoute>
        <ProfileCompletionRoute>
          <CourseAccessRoute>
            <ChapterPdfPrintPage />
          </CourseAccessRoute>
        </ProfileCompletionRoute>
      </ProtectedRoute>
    ),
    errorElement: (
      <ErrorScreen loginHref="/login" message="Unable to render the PDF export." />
    ),
  },
  // The support console: Kudu staff only. The backend decides who that is,
  // from the caller's verified Google sign-in; SupportConsoleGate asks it and
  // shows the console only on a clear "granted".
  //
  // Declared beside /app, not under it, so that someone the backend refuses
  // sees the same bare "Page not found" as any unknown address, with no app
  // shell around it. Nothing in the app links here: staff open the address.
  {
    path: '/app/support-console',
    element: (
      <ProtectedRoute>
        <SupportConsoleGate>
          <TourProvider>
            <AppShell />
          </TourProvider>
        </SupportConsoleGate>
      </ProtectedRoute>
    ),
    errorElement: <ErrorScreen loginHref="/login" message="Unable to render the app shell." />,
    children: [{ index: true, element: <SupportConsolePage /> }],
  },
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <ProfileCompletionRoute>
          <TourProvider>
            <AppShell />
          </TourProvider>
        </ProfileCompletionRoute>
      </ProtectedRoute>
    ),
    errorElement: <ErrorScreen loginHref="/login" message="Unable to render the app shell." />,
    children: [
      { index: true, element: <Navigate replace to="courses" /> },
      {
        path: 'create',
        element: <CreateCoursePage />,
        // Its steps move the address on and keep what was typed: going from
        // one to the next is not leaving the page, so no new build loads then.
        handle: CHILD_ROUTES_STAY_ON_THIS_PAGE,
        children: [
          { index: true, element: <></> },
          {
            path: ':seedCourseId',
            element: <></>,
          },
          {
            path: ':seedCourseId/:componentId',
            element: <></>,
          },
        ],
      },
      { path: 'courses', element: <CourseListPage /> },
      { path: 'profile', element: <ProfilePage /> },
      {
        path: 'courses/:courseId',
        element: (
          <CourseAccessRoute>
            <CourseHomePage />
          </CourseAccessRoute>
        ),
      },
      {
        path: 'courses/:courseId/grades',
        element: (
          <CourseAccessRoute>
            <GradebookPage />
          </CourseAccessRoute>
        ),
      },
      {
        path: 'courses/:courseId/grades/assignments/:componentUid',
        element: (
          <CourseAccessRoute>
            <AssignmentQuestionsPage />
          </CourseAccessRoute>
        ),
      },
      {
        path: 'courses/:courseId/grades/:studentId',
        element: (
          <CourseAccessRoute>
            <StudentDetailPage />
          </CourseAccessRoute>
        ),
      },
      {
        path: 'courses/:courseId/:contentId',
        element: (
          <CourseAccessRoute>
            <ContentPage />
          </CourseAccessRoute>
        ),
      },
      {
        path: 'courses/:courseId/:contentId/edit',
        element: (
          <CourseAccessRoute>
            <ContentEditRedirect />
          </CourseAccessRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

// Every page sits under the listener that loads a new build when the user
// goes to another page (issue #66).
export const routesWithUpdates = [
  { element: <LoadNewVersionOnPageChange />, children: appRoutes },
];

export const router = createBrowserRouter(routesWithUpdates);
